import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from './ThemeProvider';

interface ThemeToggleProps {
  className?: string;
}

const ToggleContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.foreground};
  transition: ${props => props.theme.transitionSmooth};
  
  &:hover {
    background: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }
`;

const DropdownContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowSoft};
  min-width: 160px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.foreground};
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: ${props => props.theme.primary};
  border-radius: 50%;
  z-index: 10;
  box-shadow: 0 0 0 2px ${props => props.theme.card};
`;

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme, isDark } = useTheme();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  const getThemeIcon = () => {
    // When system is selected, show the actual active theme icon
    if (theme === 'system') {
      return isDark ? <Moon size={16} /> : <Sun size={16} />;
    }
    
    switch (theme) {
      case 'light':
        return <Sun size={16} />;
      case 'dark':
        return <Moon size={16} />;
      default:
        return <Sun size={16} />;
    }
  };
  
  // Get the effective theme for indicator (system resolves to actual theme)
  const getEffectiveTheme = (): 'light' | 'dark' | 'system' => {
    if (theme === 'system') {
      return isDark ? 'dark' : 'light';
    }
    return theme;
  };
  
  const effectiveTheme = getEffectiveTheme();

  return (
    <ToggleContainer ref={dropdownRef} className={className}>
      <ToggleButton onClick={() => setIsOpen(!isOpen)}>
        {getThemeIcon()}
      </ToggleButton>
      <DropdownContent $isOpen={isOpen}>
        <DropdownItem onClick={() => handleThemeChange('light')}>
          <Sun size={16} />
          Light
          {theme === 'light' && <ActiveIndicator />}
        </DropdownItem>
        <DropdownItem onClick={() => handleThemeChange('dark')}>
          <Moon size={16} />
          Dark
          {theme === 'dark' && <ActiveIndicator />}
        </DropdownItem>
        <DropdownItem onClick={() => handleThemeChange('system')}>
          <Monitor size={16} />
          System
          {theme === 'system' && <ActiveIndicator />}
        </DropdownItem>
      </DropdownContent>
    </ToggleContainer>
  );
}; 